<template>
  <div v-if="isInitiative && npa.npa.status.id === 12" class="mb-6">
    <v-card
      outlined
      class="pa-0"
      style="overflow: hidden; border-left-width: 6px; border-left-style: solid;"
      :style="{ 'border-left-color': getStatusColorHex }"
    >
      <div class="d-flex align-center justify-space-between py-3 px-4 flex-wrap">

        <div class="d-flex align-center mr-4" style="min-width: 200px;">
          <v-avatar :color="statusColor" size="36" class="mr-3">
            <v-icon dark small>{{ getStatusIcon }}</v-icon>
          </v-avatar>

          <div>
            <div class="caption grey--text text--darken-1 line-height-1">
              {{ $t('Народная инициатива') }}
            </div>
            <div class="font-weight-bold d-flex align-center" :class="`${statusColor}--text`">
              {{ npa.npa.initiativeStatus.transName }}

              <v-tooltip bottom v-if="npa.npa.stopCollectionReason">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    small
                    color="grey"
                    class="ml-1"
                    v-bind="attrs"
                    v-on="on"
                  >
                    mdi-information-outline
                  </v-icon>
                </template>
                <span>
                  <strong>{{ $t('Причина остановки:') }}</strong><br>
                  {{ npa.npa.stopCollectionReason }}
                </span>
              </v-tooltip>
            </div>
            <div class="caption grey--text mt-1">
              <v-icon x-small class="mr-1">mdi-eye</v-icon>
              {{ npa.npa.view_number || 0 }} {{ $t('просмотров') }}
            </div>
          </div>
        </div>

        <div class="flex-grow-1 mx-4 d-none d-md-block">
          <div class="d-flex justify-space-between caption font-weight-medium mb-1">
            <span>{{ $t('Собрано подписей') }}</span>
            <span>
              <span :class="`${statusColor}--text text-h6`">{{ npa.npa.votesCount || 0 }}</span>
              <span class="grey--text"> / 10000</span>
            </span>
          </div>
          <v-progress-linear
            :value="(npa.npa.votesCount / 10000) * 100"
            height="6"
            rounded
            :color="statusColor"
            background-color="grey lighten-3"
          ></v-progress-linear>
        </div>

        <div class="d-flex align-center justify-end" style="min-width: 220px;">

          <div v-if="!isClosed && !isOnIntroduction" class="mr-2">
            <v-btn
              v-if="canSign"
              color="success"
              @click="signInitiative"
              :loading="signingLoading"
              rounded
              elevation="2"
              small
            >
              <v-icon left small>mdi-pen</v-icon>
              {{ $t('Подписать') }}
            </v-btn>

            <v-btn
              v-else-if="alreadySigned"
              color="grey darken-1"
              outlined
              rounded
              small
              @click="withdrawSignature"
              :loading="signingLoading"
            >
              {{ $t('Отозвать подпись') }}
              <v-icon right x-small class="ml-1">mdi-close-circle</v-icon>
            </v-btn>

            <span
              v-else-if="!canStopCollection"
              class="caption grey--text d-inline-block text-right"
              style="max-width: 150px; line-height: 1.2;"
            >
              {{ cantSignReason }}
            </span>
          </div>

          <v-chip
            v-else-if="alreadySigned"
            small
            color="grey lighten-3"
            class="mr-2"
          >
            <v-icon left small>mdi-check</v-icon>
            {{ $t('Ваш голос учтен') }}
          </v-chip>

          <div v-if="canStopCollection">
            <v-btn
              color="error"
              outlined
              rounded
              small
              @click="stopDialog = true"
            >
              {{ $t('Остановить') }}
            </v-btn>
            <v-btn
              class="ml-2"
              color="primary"
              text
              outlined
              rounded
              small
              @click="downloadSignatures"
              :loading="downloadLoading"
            >
              <v-icon left small>mdi-file-word</v-icon>
              {{ $t('Подписной лист') }}
            </v-btn>
          </div>
          <div v-else-if="isAuthorOrAdmin" class="ml-2">
            <v-btn
              color="primary"
              outlined
              rounded
              small
              @click="downloadSignatures"
              :loading="downloadLoading"
            >
              <v-icon left small>mdi-download</v-icon>
              {{ $t('Скачать итоги') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card>

    <v-dialog v-model="stopDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline error--text">
          {{ $t('Остановка сбора подписей') }}
        </v-card-title>
        <v-card-text>
          <p class="body-2">
            {{ $t('Вы собираетесь досрочно остановить сбор подписей. Пожалуйста, укажите причину.') }}
          </p>
          <v-textarea
            v-model="stopReason"
            :label="$t('Причина остановки')"
            outlined
            rows="3"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="stopDialog = false">{{ $t('Отмена') }}</v-btn>
          <v-btn
            color="error"
            @click="submitStopCollection"
            :loading="stopLoading"
            :disabled="!stopReason.trim()"
          >
            {{ $t('Остановить') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Trans } from '@/plugins/Translation'
import axios from 'axios'
export default {
  name: 'NpaInitiative',
  data() {
    return {
      baseUrl: process.env.VUE_APP_BACK_URL,
      alreadySigned: false,
      signingLoading: false,
      stopDialog: false,
      stopReason: '',
      stopLoading: false,
      downloadLoading: false,
    }
  },
  props: {
    npa: {
      type: Object,
      required: true
    }
  },
  mounted() {
    this.alreadySigned = this.npa.isSignedByUser
  },
  computed: {
    user() {
      return this.$store.state.getUser.currentUser
    },
    isInitiative() {
      return this.npa.npa && this.npa.npa.view.id === 4;
    },

    canSign() {
      return this.user
        && this.user.auth_provider === 'esia' // Поле из токена или профиля
        && !this.alreadySigned
        // && this.npa.npa.initiativeStatus.code === 'collection' // Если есть статус инициативы
        ;
    },

    cantSignReason() {
      if (!this.user) return this.$t('Для подписи нужно войти в систему');
      if (this.user.auth_provider !== 'esia') return this.$t('Для подписи нужен вход через ЕСИ Тундук');
      if (this.alreadySigned) return this.$t('Вы уже подписали');
      return '';
    },
    isClosed() {
      return this.npa.npa?.initiativeStatus?.id === 3;
    },
    isOnIntroduction() {
      return this.npa.npa?.initiativeStatus?.id === 1;
    },
    canStopCollection() {
      if (!this.user) return false;
      const statusId = this.npa.npa?.initiativeStatus?.id;

      if (statusId !== 2) return false;

      return this.npa.npa?.author?.id === this.user.id;
    },
    statusColor() {
      const statusId = this.npa.npa?.initiativeStatus?.id;
      const votes = this.npa.npa?.votesCount || 0;

      if (statusId === 2 || (statusId === 3 && votes >= 10000)) {
        return 'success';
      }

      if (statusId === 3) {
        return 'error';
      }

      return 'info';
    },

    getStatusColorHex() {
      const map = {
        'success': '#4CAF50',
        'error': '#FF5252',
        'info': '#2196F3'
      };
      return map[this.statusColor] || '#2196F3';
    },

    getStatusIcon() {
      const statusId = this.npa.npa?.initiativeStatus?.id;
      const votes = this.npa.npa?.votesCount || 0;

      if (statusId === 2) return 'mdi-account-group';

      if (statusId === 3) {
        if (votes >= 10000) return 'mdi-check-decagram'; // Или mdi-trophy

        return 'mdi-lock';
      }

      return 'mdi-eye';
    },
    isAuthorOrAdmin() {
      if (!this.user) return false;
      return this.npa.npa?.author?.id === this.user.id || this.user.adminAccess === true;
    }
  },
  methods: {
    signInitiative() {
      if (!confirm(this.$t('Вы подтверждаете подпись этой инициативы своей электронной подписью?'))) {
        return;
      }

      this.signingLoading = true;

      axios.post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/sign`)
        .then(response => {
          this.$noty.success(this.$t('Вы успешно подписали инициативу!'), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
          this.alreadySigned = true;
          if (this.npa.npa.votesCount !== undefined) {
            this.npa.npa.votesCount++;
          }
        })
        .catch(error => {
          const msg = error.response?.data?.error || 'Ошибка при подписании';
          this.$noty.error(this.$t(msg), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.signingLoading = false;
        });
    },

    withdrawSignature() {
      if (!confirm(this.$t('Вы действительно хотите отозвать свою подпись?'))) {
        return;
      }

      this.signingLoading = true;

      axios.post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/withdraw`)
        .then(response => {
          this.$noty.info(this.$t('Подпись отозвана'), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
          this.alreadySigned = false;
          if (this.npa.npa.votesCount > 0) {
            this.npa.npa.votesCount--;
          }
        })
        .catch(error => {
          this.$noty.error(this.$t('Ошибка при отзыве подписи'), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.signingLoading = false;
        });
    },
    submitStopCollection() {
      if (!this.stopReason.trim()) return;

      if (!confirm(this.$t('Вы уверены? Сбор подписей будет закрыт.'))) {
        return;
      }

      this.stopLoading = true;

      axios.post(`${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/stop`, {
        reason: this.stopReason
      })
        .then(response => {
          this.$noty.success(this.$t('Сбор подписей успешно остановлен'), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
          this.stopDialog = false;
          this.stopReason = '';

          this.$emit('fetch-data');
        })
        .catch(error => {
          const msg = error.response?.data?.error || 'Ошибка при остановке сбора';
          this.$noty.error(this.$t(msg), {
            killer: true,
            timeout: 1000,
            layout: 'topRight'
          });
        })
        .finally(() => {
          this.stopLoading = false;
        });
    },
    downloadSignatures() {
      this.downloadLoading = true;
      const url = `${this.baseUrl}${Trans.currentLocale}/api/initiative/${this.npa.npa.id}/export-signatures`;

      axios({
        url: url,
        method: 'GET',
        responseType: 'blob', // Важно для скачивания файлов
      }).then((response) => {
        // Создаем ссылку для скачивания
        const href = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `signatures_${this.npa.npa.id}.docx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }).catch((error) => {
        this.$message.error(this.$t('Ошибка при скачивании файла'));
      }).finally(() => {
        this.downloadLoading = false;
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.line-height-1 {
  line-height: 1;
}
</style>
